import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Bot, Send, Plus, X, Loader2, Settings } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: "#1a1a1a",
  secondary: "#f5f0eb",
  accent: "#d4c5b5",
  text: "#1a1a1a",
  textLight: "#6b6b6b",
  white: "#ffffff",
  border: "#e8e0d8",
};

const SUGGESTIONS = [
  "Angebot erstellen",
  "Rechnung prüfen",
  "Betriebsausgabe buchen",
  "Reisekosten berechnen",
  "Steuertipp",
  "Kunde nachfassen",
];

export default function UniversalAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [configured, setConfigured] = useState(null);
  const [providerInfo, setProviderInfo] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Draggable button position
  const [btnPos, setBtnPos] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem("ki_btn_pos"));
      if (p && typeof p.x === "number" && typeof p.y === "number") return p;
    } catch {}
    return null;
  });
  const dragRef = useRef(null);

  const clampPos = (x, y) => ({
    x: Math.min(Math.max(x, 8), window.innerWidth - 64),
    y: Math.min(Math.max(y, 8), window.innerHeight - 64),
  });

  const onBtnPointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onBtnPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < 8) return;
    d.moved = true;
    setBtnPos(clampPos(d.origX + dx, d.origY + dy));
  };
  const onBtnPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.moved) {
      setBtnPos((p) => {
        if (p) { try { localStorage.setItem("ki_btn_pos", JSON.stringify(p)); } catch {} }
        return p;
      });
    } else {
      setIsOpen(true);
    }
  };

  // Check if configured on mount
  useEffect(() => {
    axios.get(`${API}/ki-assistent/settings`).then((r) => {
      setConfigured(r.data.configured);
      if (r.data.provider === "openai") setProviderInfo("OpenAI");
      else if (r.data.provider === "anthropic") setProviderInfo("Claude");
    }).catch(() => setConfigured(false));
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  const sendMessage = useCallback(async (messageText) => {
    const text = (messageText || input).trim();
    if (!text || isLoading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/ki-assistent/chat`, {
        message: text,
        conversation_id: conversationId,
      });
      setConversationId(res.data.conversation_id);
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.message }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Verbindungsfehler – bitte erneut versuchen.";
      setMessages((prev) => [...prev, { role: "assistant", content: `⚠️ ${detail}`, isError: true }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, conversationId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const resetChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  // Don't show if not configured
  if (configured === false) return null;
  if (configured === null) return null;

  const btnStyle = btnPos
    ? { position: "fixed", left: btnPos.x, top: btnPos.y, zIndex: 9999 }
    : { position: "fixed", bottom: 24, right: 24, zIndex: 9999 };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <div style={btnStyle}
          onPointerDown={onBtnPointerDown} onPointerMove={onBtnPointerMove} onPointerUp={onBtnPointerUp}
          data-testid="ki-assistant-btn">
          <div style={{
            width: 56, height: 56, borderRadius: "50%", background: COLORS.primary,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            transition: "transform 0.2s",
          }}>
            <Bot style={{ width: 24, height: 24, color: COLORS.white }} />
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, width: 380, maxHeight: "70vh",
          borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
          display: "flex", flexDirection: "column", zIndex: 10000,
          border: `1px solid ${COLORS.border}`, background: COLORS.white,
        }} data-testid="ki-assistant-panel">
          {/* Header */}
          <div style={{
            padding: "16px 20px", background: COLORS.primary, color: COLORS.white,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Bot style={{ width: 20, height: 20 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>KI-Assistent</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>{providerInfo || "Business Studio Suite"}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {conversationId && (
                <button onClick={resetChat} style={{
                  background: "none", border: "none", color: COLORS.white, cursor: "pointer",
                  fontSize: 12, opacity: 0.7, padding: "4px 8px", borderRadius: 4,
                }}>
                  <Plus style={{ width: 14, height: 14, display: "inline", marginRight: 2 }} /> Neu
                </button>
              )}
              <button onClick={() => setIsOpen(false)} style={{
                background: "none", border: "none", color: COLORS.white, cursor: "pointer",
                fontSize: 20, lineHeight: 1, opacity: 0.7, padding: 4,
              }}>
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 12, minHeight: 300, maxHeight: "50vh" }}>
            {messages.length === 0 && !isLoading && (
              <div style={{ textAlign: "center", padding: "30px 20px", color: COLORS.textLight, fontSize: 14, lineHeight: 1.6 }}>
                <Bot style={{ width: 32, height: 32, margin: "0 auto 12px", opacity: 0.4 }} />
                <strong>Hallo! Ich bin dein KI-Assistent.</strong><br />
                Ich helfe dir bei allem rund um dein Business – Angebote, Rechnungen, Steuern, Kunden und Reisen.
              </div>
            )}
            {messages.length === 0 && !isLoading && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)} style={{
                    padding: "8px 14px", borderRadius: 20, border: `1px solid ${COLORS.border}`,
                    background: COLORS.secondary, color: COLORS.text, fontSize: 12, cursor: "pointer",
                  }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} style={{
                maxWidth: "85%", padding: "12px 16px",
                borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                background: msg.role === "user" ? COLORS.primary : COLORS.secondary,
                color: msg.role === "user" ? COLORS.white : COLORS.text,
                alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                fontSize: 14, lineHeight: 1.5, wordBreak: "break-word", whiteSpace: "pre-wrap",
                border: msg.role === "user" ? "none" : `1px solid ${COLORS.border}`,
                ...(msg.isError ? { borderColor: "#e74c3c", color: "#e74c3c" } : {}),
              }}>
                {msg.content}
              </div>
            ))}
            {isLoading && (
              <div style={{ display: "flex", gap: 4, padding: "12px 16px", alignSelf: "flex-start" }}>
                <Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} />
                <span style={{ fontSize: 13, color: COLORS.textLight }}>Denkt nach…</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: "16px 20px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 10, alignItems: "flex-end" }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Frage eingeben…"
              rows={1}
              disabled={isLoading}
              style={{
                flex: 1, padding: "12px 16px", borderRadius: 12, border: `1px solid ${COLORS.border}`,
                fontSize: 14, outline: "none", resize: "none", fontFamily: "inherit",
                maxHeight: 120, minHeight: 44, background: COLORS.secondary, color: COLORS.text,
              }}
              data-testid="ki-assistant-input"
            />
            <button onClick={() => sendMessage()} disabled={isLoading || !input.trim()} style={{
              width: 44, height: 44, borderRadius: 12, background: COLORS.primary, color: COLORS.white,
              border: "none", cursor: isLoading || !input.trim() ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              opacity: isLoading || !input.trim() ? 0.5 : 1,
            }} data-testid="ki-assistant-send">
              <Send style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </div>
      )}

      {/* Spin animation for loader */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
